import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { StudySearchFilters } from '../../../services/studies-api.service';

@Component({
    selector: 'app-study-search-form',
    templateUrl: './study-search-form.component.html',
    styleUrls: ['./study-search-form.component.scss'],
    standalone: false
})
export class StudySearchFormComponent implements OnInit, OnChanges {
  @Input() isLoading = false;
  @Input() initialPatientName: string | null = null;
  @Input() initialPatientId: string | null = null;
  @Output() search = new EventEmitter<StudySearchFilters>();
  @Output() clear = new EventEmitter<void>();

  searchForm: UntypedFormGroup;
  private lastInitializedPatientName: string | null = null;
  private lastInitializedPatientId: string | null = null;

  constructor(private fb: UntypedFormBuilder) {
    this.searchForm = this.fb.group({
      patientBirthDate: [null],
      patientName: [''],
      patientId: [''],
      institutionName: ['']
    });
  }

  ngOnInit(): void {
    if (this.initialPatientId && this.initialPatientId !== this.lastInitializedPatientId) {
      this.setInitialPatientIdAndSearch();
    } else if (this.initialPatientName && this.initialPatientName !== this.lastInitializedPatientName) {
      this.setInitialPatientNameAndSearch();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialPatientId'] &&
        changes['initialPatientId'].currentValue &&
        changes['initialPatientId'].currentValue !== this.lastInitializedPatientId) {
      setTimeout(() => this.setInitialPatientIdAndSearch(), 0);
    } else if (changes['initialPatientName'] &&
        changes['initialPatientName'].currentValue &&
        changes['initialPatientName'].currentValue !== this.lastInitializedPatientName) {
      setTimeout(() => this.setInitialPatientNameAndSearch(), 0);
    }
  }

  private setInitialPatientIdAndSearch(): void {
    if (this.initialPatientId && this.initialPatientId !== this.lastInitializedPatientId) {
      this.searchForm.patchValue({ patientId: this.initialPatientId, patientName: '' });
      this.lastInitializedPatientId = this.initialPatientId;
      this.onSearch();
    }
  }

  private setInitialPatientNameAndSearch(): void {
    if (this.initialPatientName && this.initialPatientName !== this.lastInitializedPatientName) {
      this.searchForm.patchValue({ patientName: this.initialPatientName });
      this.lastInitializedPatientName = this.initialPatientName;
      this.onSearch();
    }
  }

  onSearch(): void {
    if (this.searchForm.valid) {
      const formValue = this.searchForm.value;
      const filters: StudySearchFilters = {
        patientName: this.normalizePatientName(formValue.patientName),
        patientId: formValue.patientId?.trim() || undefined,
        institutionName: formValue.institutionName?.trim() || undefined
      };

      this.search.emit(filters);
    }
  }

  /**
   * Normalize patient name input for Orthanc search.
   * DICOM stores names uppercase with "^" as delimiter (e.g. MARSH^STANLEY).
   * This strips special characters (^, ,, .) and joins meaningful tokens
   * with "*" so the BFF wildcard query matches regardless of delimiter or case.
   * e.g. "marsh^stanley" → "MARSH*STANLEY", sent as "*MARSH*STANLEY*"
   */
  private normalizePatientName(raw: string | null | undefined): string | undefined {
    if (!raw) return undefined;
    const tokens = raw
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, ' ')  // replace special chars with space
      .split(/\s+/)                    // split on whitespace
      .filter(t => t.length > 0);
    return tokens.length > 0 ? tokens.join('*') : undefined;
  }

  onClear(): void {
    this.searchForm.reset();
    this.clear.emit();
    this.onSearch();
  }
}
